import { Edit } from "@mui/icons-material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Avatar, Box, IconButton } from "@mui/material";
import type React from "react";
import { type FC, useCallback, useRef, useState } from "react";
import { trackEvent } from "../../../../utils/analytics";

type Props = {
	imageURL?: string;
	isEditable: boolean;
	onClicked: () => void;
	onFileChange: (file: File) => void;
	avatarSizeDesktop: number;
	avatarSizeMobile: number;
	clickEventName?: string;
	clickEventSourceId?: string | number;
};

export const ImagePicker: FC<Props> = ({
	imageURL,
	onClicked,
	isEditable,
	onFileChange,
	avatarSizeDesktop,
	avatarSizeMobile,
	clickEventName,
	clickEventSourceId,
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [previewSrc, setPreviewSrc] = useState<string | null>(null);

	const handleImageEditClick = useCallback(() => {
		trackEvent(`${clickEventName}_image_picker_click`, {
			[`${clickEventName}_id`]: clickEventSourceId,
		});
		fileInputRef.current?.click();
		onClicked();
	}, [onClicked, clickEventName, clickEventSourceId]);

	const previewFile = useCallback((file: File) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onloadend = () => {
			if (typeof reader.result === "string") {
				setPreviewSrc(reader.result);
			}
		};
	}, []);

	const handleFileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.files) {
				const fileAdded = e.target.files[0];
				if (fileAdded) {
					previewFile(fileAdded);
					onFileChange(fileAdded);
				}
			}
		},
		[previewFile, onFileChange],
	);

	return (
		<Box
			data-testid="profile-image-picker"
			style={{
				position: "relative",
			}}
			sx={{
				width: {
					xs: avatarSizeMobile + 10,
					md: avatarSizeDesktop + 10,
				},
				height: {
					xs: avatarSizeMobile + 10,
					md: avatarSizeDesktop + 10,
				},
			}}
		>
			{imageURL || previewSrc ? (
				<Avatar
					src={previewSrc || imageURL}
					sx={{
						width: {
							xs: avatarSizeMobile,
							md: avatarSizeDesktop,
						},
						height: {
							xs: avatarSizeMobile,
							md: avatarSizeDesktop,
						},
					}}
				/>
			) : (
				<AccountCircleIcon
					sx={{
						fontSize: {
							xs: `${avatarSizeMobile}px`,
							md: `${avatarSizeDesktop}px`,
						},
					}}
				/>
			)}
			{isEditable && (
				<IconButton
					data-testid="profile-image-edit-button"
					size={"small"}
					onClick={handleImageEditClick}
					style={{
						position: "absolute",
						right: 0,
						bottom: 0,
						zIndex: 10,
					}}
				>
					<Edit fontSize={"inherit"} />
				</IconButton>
			)}
			<input
				data-testid="profile-image-file-input"
				title="profile-image"
				type="file"
				ref={fileInputRef}
				style={{ display: "none" }}
				onChange={handleFileChange}
			/>
		</Box>
	);
};
